import {ActionList, ActionMenu, IconButton} from '@primer/react'
import {AgentIcon, AlertIcon, CommentDiscussionIcon, InfoIcon, KebabHorizontalIcon} from '@primer/octicons-react'
import {useCallback, useRef, useState} from 'react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {DockedPanel, useDockedPanelActions} from '../contexts/DockedPanelContext'
import {usePullRequestAnalytics} from '../hooks/use-pull-request-analytics'
import {useUnresolvedConversationCount} from '../stores/layout-selectors'
import type {PullRequestsEventType, PullRequestsTargetType} from '../types/analytics-events-types'
import styles from './MiniHeaderMoreMenu.module.css'
import {ensurePreviousActiveDialogIsClosed} from '@github-ui/diff-lines/ensure-previous-active-dialog-is-closed'

/**
 * Overflow menu rendered in the PullRequestMiniHeader. Mirrors the header's
 * panel-launcher buttons that are hidden by responsive utilities at narrow
 * viewports — selecting an item toggles the same docked panel that the
 * corresponding header button would.
 *
 * Visibility rules (see MiniHeaderMoreMenu.module.css):
 * - The kebab itself is hidden when no items would be visible (>= 768px).
 * - Each item is shown only at the breakpoint where its header button is
 *   hidden, so the menu and the header buttons never duplicate each other.
 */
export function MiniHeaderMoreMenu({size = 'small'}: {size?: 'small' | 'medium'}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const actions = useDockedPanelActions()
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  const unresolvedConversationCount = useUnresolvedConversationCount() ?? 0
  const isAgentSessionsEnabled = isFeatureEnabled('coding_agent_logs_in_pr')
  const overviewItemLabel = isFeatureEnabled('updated_pull_request_header') ? 'Description' : 'Overview'

  const openPanel = useCallback(
    (panel: DockedPanel, event: PullRequestsEventType, analytics_surface: PullRequestsTargetType) => {
      sendPullRequestAnalyticsEvent(event, analytics_surface)
      ensurePreviousActiveDialogIsClosed()
      setIsMenuOpen(false)
      actions.togglePanel(panel, {returnFocusTo: menuButtonRef.current})
    },
    [actions, sendPullRequestAnalyticsEvent],
  )

  return (
    <>
      <ActionMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <ActionMenu.Anchor>
          <IconButton
            ref={menuButtonRef}
            icon={KebabHorizontalIcon}
            variant="default"
            size={size}
            aria-label="More options"
            className={styles.moreMenu}
          />
        </ActionMenu.Anchor>
        <ActionMenu.Overlay align="end">
          <ActionList>
            <ActionList.Item
              className={styles.itemHiddenAtMedium}
              onSelect={() => openPanel(DockedPanel.Overview, 'overview_side_panel.open', 'OVERVIEW_SIDE_PANEL_BUTTON')}
            >
              <ActionList.LeadingVisual>
                <InfoIcon />
              </ActionList.LeadingVisual>
              {overviewItemLabel}
            </ActionList.Item>
            {isAgentSessionsEnabled && (
              <ActionList.Item
                className={styles.itemHiddenAtMedium}
                onSelect={() =>
                  openPanel(DockedPanel.AgentSessions, 'agent_sessions_side_panel.open', 'AGENT_SESSIONS_MENU_ITEM')
                }
              >
                <ActionList.LeadingVisual>
                  <AgentIcon />
                </ActionList.LeadingVisual>
                Agent sessions
              </ActionList.Item>
            )}
            <ActionList.Item
              className={styles.itemHiddenAtSmall}
              onSelect={() => openPanel(DockedPanel.Comments, 'comments_side_panel.open', 'COMMENTS_SIDE_PANEL_BUTTON')}
            >
              <ActionList.LeadingVisual>
                <CommentDiscussionIcon />
              </ActionList.LeadingVisual>
              Comments
              {unresolvedConversationCount > 0 && (
                <ActionList.TrailingVisual>{unresolvedConversationCount}</ActionList.TrailingVisual>
              )}
            </ActionList.Item>
            <ActionList.Item
              className={styles.itemHiddenAtSmall}
              onSelect={() => openPanel(DockedPanel.Alerts, 'alerts_side_panel.open', 'ALERTS_SIDE_PANEL_BUTTON')}
            >
              <ActionList.LeadingVisual>
                <AlertIcon />
              </ActionList.LeadingVisual>
              Alerts
            </ActionList.Item>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </>
  )
}
